/**
 * iter_225 · Phase 22.0 — 📊 Feature Pulse (real analytics)
 *
 * REPLACES the previous "Boosted Reach + Impressions × 8" widget with
 * event-based metrics sourced from real user activity:
 *   • Feature Impressions — deduped views of Featured placements
 *   • Feature Clicks     — real clicks on Featured toasts / cards
 *   • Real Plays         — canonical `play_count` (unchanged)
 *   • Plays from Feature — plays attributed to a Featured surface
 *
 * The scheduler's promotional drip is now surfaced as a CAMPAIGN
 * STATUS chip (Active / Frozen), never as "audience reached".
 *
 * Legacy `reach` and `impressions × 8` numbers are still on the
 * response for backward-compat with other surfaces but are NOT
 * rendered here.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import NowFeaturedShareModal from "@/components/NowFeaturedShareModal";
import { MetricInfoIcon } from "@/components/MetricTooltip";
import { optimizeImage } from "@/lib/mediaUrls";

const fmt = (n) => Number(n || 0).toLocaleString();
const pct = (n) => `${Math.round((Number(n || 0)) * 1000) / 10}%`;
const REFRESH_INTERVAL_MS = 20_000;

const CampaignPill = ({ campaign, status }) => {
  const isSpotlight = status === "spotlight";
  const active = !!campaign?.active;
  const label = active
    ? (isSpotlight ? "🌟 Spotlight campaign active" : "🚀 Feature campaign active")
    : "⏸ Campaign frozen";
  const tone = active
    ? "bg-emerald-500/20 border-emerald-400/40 text-emerald-100"
    : "bg-white/5 border-white/20 text-gray-300";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider border ${tone}`}
      data-testid="feature-pulse-campaign-pill"
    >
      {label}
    </span>
  );
};

const FeaturePulse = () => {
  const [data, setData] = useState(null);
  const [pulseId, setPulseId] = useState(0);
  const [shareTarget, setShareTarget] = useState(null);
  const prevImpressionsRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get("/me/boost-pulse");
        if (cancelled || !mountedRef.current) return;
        const impressions = res.data?.totals?.real_impressions || 0;
        if (impressions !== prevImpressionsRef.current) {
          setPulseId((p) => p + 1);
          prevImpressionsRef.current = impressions;
        }
        setData(res.data);
      } catch (_e) { /* silent */ }
    };
    load();
    const id = setInterval(load, REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      mountedRef.current = false;
      clearInterval(id);
    };
  }, []);

  if (!data || !data.tracks || data.tracks.length === 0) return null;

  const t = data.totals || {};
  const totalImpressions = t.real_impressions || 0;
  const totalClicks = t.feature_clicks || 0;
  const totalPlays = t.real_plays || 0;
  const totalFeaturePlays = t.feature_plays || 0;
  const ctr = t.ctr || 0;
  const hasFeaturePlays = totalFeaturePlays > 0;

  return (
    <div
      data-testid="feature-pulse-widget"
      className="relative rounded-2xl border border-emerald-400/30 bg-gradient-to-br from-emerald-500/10 via-cyan-500/10 to-purple-500/15 p-4 sm:p-5 mb-6 overflow-hidden"
    >
      {/* Gold pulse ring — re-mounted on each tick to replay the animation */}
      <span
        key={pulseId}
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-2xl ring-2 ring-emerald-300/60"
        style={{ animation: "feature-pulse-glow 1500ms ease-out forwards" }}
      />

      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap relative">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[3px] font-black text-emerald-200">
            📊 Feature Pulse
          </p>
          <p className="text-xs text-cyan-100/70 mt-0.5">
            Real activity from your Featured placements · refreshed every {Math.round(REFRESH_INTERVAL_MS / 1000)}s
          </p>
        </div>
        <div className="flex items-center gap-2 self-start">
          {data.tracks?.length > 0 && (
            <button
              onClick={() => setShareTarget(data.tracks[0])}
              className="text-[10px] px-2.5 py-1 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white font-bold shadow-md shadow-emerald-500/30 flex items-center gap-1"
              data-testid="feature-pulse-share-btn"
            >
              <span>🎖️</span>
              <span>Share Feature</span>
            </button>
          )}
          <span
            className="text-[9px] uppercase tracking-widest text-emerald-200/70"
            data-testid="feature-pulse-live-dot"
          >
            <span
              className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1.5 align-middle"
              style={{ animation: "feature-pulse-dot 1500ms ease-in-out infinite" }}
            />
            LIVE
          </span>
        </div>
      </div>

      {/* Totals — real, event-based only */}
      <div className={`grid ${hasFeaturePlays ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"} gap-2 mt-4 relative`}>
        <Stat
          icon="👁"
          label="Feature Impressions"
          value={fmt(totalImpressions)}
          tone="emerald"
          metric="impressions"
          testid="feature-pulse-total-impressions"
        />
        <Stat
          icon="👆"
          label="Feature Clicks"
          value={fmt(totalClicks)}
          tone="cyan"
          sub={totalImpressions > 0 ? `CTR ${pct(ctr)}` : "—"}
          testid="feature-pulse-total-clicks"
        />
        <Stat
          icon="▶"
          label="Real Plays"
          value={fmt(totalPlays)}
          tone="purple"
          metric="plays"
          testid="feature-pulse-total-real-plays"
        />
        {hasFeaturePlays && (
          <Stat
            icon="🎯"
            label="Plays from Feature"
            value={fmt(totalFeaturePlays)}
            tone="amber"
            sub="attributed"
            testid="feature-pulse-total-feature-plays"
          />
        )}
      </div>

      {/* Per-track rows */}
      <div className="mt-4 space-y-2 relative" data-testid="feature-pulse-tracks">
        {data.tracks.map((tr) => (
          <Link
            key={tr.submission_id}
            to={`/track/${tr.submission_id}`}
            className="flex items-center gap-3 px-3 py-2 rounded-xl border border-emerald-400/20 bg-black/30 hover:bg-black/45 transition-colors"
            data-testid={`feature-pulse-track-${tr.submission_id}`}
          >
            {tr.cover_url ? (
              <img
                src={optimizeImage(tr.cover_url, 'thumb')}
                alt=""
                className="w-10 h-10 rounded object-cover flex-shrink-0"
                onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
              />
            ) : (
              <div className="w-10 h-10 rounded bg-white/5" />
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                <p className="text-sm font-bold text-white truncate">{tr.track_title}</p>
                <CampaignPill campaign={tr.campaign} status={tr.status} />
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]">
                <span className="text-emerald-200" data-testid={`feature-pulse-imp-${tr.submission_id}`}>
                  👁 {fmt(tr.real_impressions)} imp.
                </span>
                <span className="text-cyan-200">
                  👆 {fmt(tr.feature_clicks)} clicks
                </span>
                <span className="text-purple-200/80">
                  ▶ {fmt(tr.real_plays)} plays
                </span>
                {tr.feature_plays > 0 && (
                  <span className="text-amber-200/90">
                    🎯 {fmt(tr.feature_plays)} from feature
                  </span>
                )}
                {tr.real_impressions > 0 && tr.feature_clicks > 0 && (
                  <span className="text-gray-400">
                    · CTR {pct(tr.ctr)}
                  </span>
                )}
              </div>
              {/* Campaign progress — labelled honestly */}
              {tr.campaign?.target_credits > 0 && (
                <div className="mt-1">
                  <div className="h-1 rounded-full bg-white/5 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-emerald-400 via-cyan-400 to-purple-400 transition-[width] duration-1000"
                      style={{ width: `${tr.campaign.progress_pct}%` }}
                      data-testid={`feature-pulse-campaign-progress-${tr.submission_id}`}
                    />
                  </div>
                  <p className="text-[9px] text-gray-500 mt-0.5">
                    Campaign delivery progress · {tr.campaign.progress_pct}%
                  </p>
                </div>
              )}
            </div>
            <span className="text-[9px] font-black uppercase tracking-wider text-emerald-300/80 self-center">
              →
            </span>
          </Link>
        ))}
      </div>

      <p className="text-[10px] text-gray-500 mt-3 relative">
        <span data-testid="feature-pulse-metrics-note">
          All numbers above are real, deduped events. Campaign progress reflects promotional distribution, not audience size.
        </span>
      </p>

      <style>{`
        @keyframes feature-pulse-glow {
          0%   { opacity: 0.9; box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.55), inset 0 0 0 0 rgba(52, 211, 153, 0.20); }
          70%  { opacity: 0.4; box-shadow: 0 0 0 18px rgba(52, 211, 153, 0),     inset 0 0 0 18px rgba(52, 211, 153, 0); }
          100% { opacity: 0;   box-shadow: 0 0 0 0 rgba(52, 211, 153, 0); }
        }
        @keyframes feature-pulse-dot {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50%      { opacity: 1;   transform: scale(1.35); }
        }
      `}</style>

      <NowFeaturedShareModal
        open={!!shareTarget}
        onOpenChange={(o) => { if (!o) setShareTarget(null); }}
        submission={shareTarget ? {
          submission_id: shareTarget.submission_id,
          track_title: shareTarget.track_title,
          cover_url: shareTarget.cover_url,
          cover_art: shareTarget.cover_url,
          // The share modal previously consumed reach × 8 impressions.
          // Feed it the REAL impressions + the campaign target so it
          // renders honest numbers on the "Now Featured" card.
          boost_applied: shareTarget.real_impressions || 0,
          floor_applied: 0,
          impressions: shareTarget.real_impressions || 0,
          status: shareTarget.status,
          user_id: data?.user_id,
        } : null}
        artist={data?.artist || null}
      />
    </div>
  );
};

const Stat = ({ icon, label, value, sub, tone, testid, metric }) => {
  const toneCls = {
    emerald: "text-emerald-100 border-emerald-400/30 bg-emerald-500/10",
    cyan: "text-cyan-100 border-cyan-400/30 bg-cyan-500/10",
    purple: "text-purple-100 border-purple-400/30 bg-purple-500/10",
    amber: "text-amber-100 border-amber-400/30 bg-amber-500/10",
  }[tone] || "text-white border-white/10 bg-white/5";
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border px-3 py-2 ${toneCls}`}
    >
      <p className="text-[9px] uppercase tracking-wider font-black opacity-80 flex items-center gap-1">
        <span>{icon}</span>
        <span>{label}</span>
        {metric && <MetricInfoIcon metric={metric} className="ml-0.5" />}
      </p>
      <p className="text-xl font-black mt-0.5 leading-none">{value}</p>
      {sub && <p className="text-[9px] opacity-70 mt-0.5">{sub}</p>}
    </div>
  );
};

export default FeaturePulse;
